import { useStorageConditions } from '@/hooks/useStorage'
import { profileDisplayName } from '@/lib/keptAt'
import { cn } from '@/lib/utils'
import type { StorageCondition, TemperatureFamily } from '@/types/storage'
import { FAMILY_TONE } from './ConditionControls'

/**
 * The dot's solid fill — `FAMILY_TONE` is a soft surface + ink pair, not a
 * fill. Exported so the Kept-at menu's plain rows (`KeptAtCell`) can colour
 * their own dot the same way without reaching into `ConditionControls.tsx`.
 */
export const FAMILY_DOT: Record<TemperatureFamily, string> = {
  CRYOGENIC: 'bg-purple-500',
  ULTRA_LOW: 'bg-sky-500',
  FREEZER: 'bg-blue-500',
  REFRIGERATED: 'bg-teal-500',
  AMBIENT_CRT: 'bg-amber-500',
}

/**
 * The "Kept at" chip — dot + temperature + condition name, e.g. "−80 °C
 * Frozen" — everywhere storage UI shows what a unit or location is kept at.
 *
 * Replaces `ConditionBadge` in this redesign: same reference data
 * (`useStorageConditions`), same family colouring, but temperature-led by
 * default, because "how cold" is the question this app answers constantly and
 * the name is secondary context.
 */
export function KeptAtBadge({
  condition,
  temperatureFirst = true,
}: {
  condition: StorageCondition | null
  temperatureFirst?: boolean
}) {
  const reference = useStorageConditions()
  const meta = reference.data?.conditions.find((c) => c.name === condition)

  if (!condition) {
    // Not "unknown" — an unset Kept-at is a real state with a consequence:
    // nothing that states a requirement can be put away here.
    return <span className='text-xs text-muted-foreground'>Not set</span>
  }

  // Deduped: a condition's own label often repeats its temperature (e.g.
  // "Cryogenic -150 C"), which next to the temperature itself read as
  // "≤ −150 °C Cryogenic -150 C". Only the display changes — `meta.label`
  // itself is untouched.
  const name = profileDisplayName(meta?.label ?? condition)
  const temperature = meta?.temperature
  const tone = meta?.family ? FAMILY_TONE[meta.family] : 'border-border bg-muted text-muted-foreground'
  const dot = meta?.family ? FAMILY_DOT[meta.family] : 'bg-muted-foreground'

  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-semibold', tone)}>
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dot)} />
      {temperatureFirst ? (
        <>
          {temperature || name}
          {temperature && <span className='font-normal opacity-80'>· {name}</span>}
        </>
      ) : (
        <>
          {name}
          {temperature && <span className='font-normal opacity-80'>· {temperature}</span>}
        </>
      )}
    </span>
  )
}
